import { useRef, useState } from 'react'
import { api } from '../../lib/api'
import { useI18n } from '../../hooks/useI18n'
import { setProfileLocally, useSession } from '../../hooks/useSession'
import { showToast } from '../../lib/toast'
import { resolveAvatar } from '../../lib/avatar'
import Button from '../Button'

/**
 * Name and picture — the FIRST onboarding screen, and the web twin of
 * WechatProfilePrompt.jsx.
 *
 * Same contract as the mini program, and the same two decisions:
 *
 *   THE NAME IS REQUIRED. It is what other members see at the table.
 *   THE PICTURE IS NOT. A gender-aware placeholder is already assigned
 *   server-side (AvatarPlaceholderService), so demanding a photo makes that
 *   system pointless and puts a wall on the very first screen.
 *
 * The only difference from WeChat is where the values come from — there, the
 * platform supplies them via `open-type="chooseAvatar"` and
 * `<input type="nickname">`, which cannot be triggered programmatically. Here
 * the member picks a file and types a name. Everything after this point is
 * identical: same PATCH /me, same POST /me/avatar.
 */

const MAX_BYTES = 5 * 1024 * 1024
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp']

/** Read as base64 WITHOUT the `data:image/png;base64,` prefix — the backend wants the payload only. */
const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result || '')
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

export default function ProfileSetupPanel({ onDone }: { onDone: () => void }) {
  const { t, lang } = useI18n()
  const { profile } = useSession()

  const [name, setName] = useState(profile?.display_name || '')
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string>(resolveAvatar(profile?.avatar_url))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const pick = (chosen: File | undefined) => {
    if (!chosen) return
    if (!ACCEPTED.includes(chosen.type)) {
      setError(lang === 'zh' ? '请选择 PNG、JPG 或 WebP 图片' : 'Choose a PNG, JPG or WebP image')
      return
    }
    if (chosen.size > MAX_BYTES) {
      setError(lang === 'zh' ? '图片请小于 5MB' : 'Please choose an image under 5MB')
      return
    }
    setError(null)
    setFile(chosen)
    setPreview(URL.createObjectURL(chosen))
  }

  const save = async () => {
    if (saving) return
    if (!name.trim()) {
      setError(t('wxp.needName'))
      return
    }
    setSaving(true)
    setError(null)

    // The upload is attempted SEPARATELY and is never allowed to abort the
    // save. If OSS is down, the name and the granted flag must still persist —
    // otherwise this screen reappears forever with nothing saved at all, which
    // is exactly the trap the mini program hit.
    let avatarFailed = false
    if (file) {
      try {
        await api.uploadAvatar(file.type, await toBase64(file))
      } catch (uploadError) {
        avatarFailed = true
        console.error('[tc][profile-setup] avatar upload failed', uploadError)
      }
    }

    try {
      const updated = await api.updateMe({
        display_name: name.trim(),
        wechat_profile_prompted: true,
        wechat_profile_granted_at: new Date().toISOString()
      } as never)
      setProfileLocally(updated)
      showToast({
        title: avatarFailed ? t('wxp.avatarFailed') : t('wxp.saved'),
        icon: avatarFailed ? 'none' : 'success'
      })
      onDone()
    } catch (saveError) {
      const message =
        (saveError as { data?: { message?: string }; message?: string })?.data?.message
        || (saveError as { message?: string })?.message
        || t('wxp.failedDetail')
      console.error('[tc][profile-setup] save failed', saveError)
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <h1 className="mt-6 font-display text-hero leading-tight text-ivory">{t('wxp.titleReq')}</h1>
      <p className="mt-2 text-13 leading-relaxed text-muted">
        {lang === 'zh'
          ? '添加你的名字，其他成员在餐桌上会看到它。照片可选。'
          : 'Add your name — it is what other members see at the table. A picture is optional.'}
      </p>

      <div className="mt-6 flex items-center gap-4">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="relative h-20 w-20 shrink-0 overflow-hidden rounded-full border border-gold-dim bg-card-2"
          aria-label={t('wxp.avatarHint')}
        >
          {preview
            ? <img src={preview} alt="" className="h-full w-full object-cover" />
            : <span className="flex h-full w-full items-center justify-center text-hero text-gold">+</span>}
        </button>
        <div className="min-w-0 flex-1">
          <input
            value={name}
            onChange={(e) => { setError(null); setName(e.target.value) }}
            placeholder={t('wxp.namePlaceholder')}
            maxLength={40}
            className="h-12 w-full rounded-sm border border-gold-dim bg-card px-3 text-md text-ivory placeholder:text-muted-2 focus:border-gold focus:outline-none"
          />
          <p className="mt-1.5 text-2xs text-muted-2">{t('wxp.avatarHint')}</p>
        </div>
      </div>

      {/* `capture` is deliberately absent: on a phone the file picker then
          offers BOTH the camera and the photo library, which is what people
          expect. Forcing the camera is the common mistake here. */}
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED.join(',')}
        className="hidden"
        onChange={(e) => pick(e.target.files?.[0])}
      />

      {error ? <p className="mt-3 text-13 text-[#e08a6a]">{error}</p> : null}

      <div className="mt-6">
        <Button loading={saving} onClick={save}>{t('wxp.continue')}</Button>
      </div>
    </div>
  )
}

/**
 * Has this member already been through the name/picture screen?
 *
 * Same field the mini program sets, so someone who signed up on WeChat and
 * later opens the web app is NOT asked again.
 */
export const needsProfileSetup = (profile: { display_name?: unknown; wechat_profile_granted_at?: unknown } | null) => {
  if (!profile) return false
  if (profile.wechat_profile_granted_at) return false
  return !String(profile.display_name || '').trim()
}
